import { Stack, Text } from '@mantine/core'
import dayjs from 'dayjs'
import React from 'react'
import { useTheme } from '../../../components/ThemeProvider'
import { CompanyItem } from './types'

interface CompanyCardProps {
  company: CompanyItem
  index: number
}

const relationshipLabelMap: Record<string, string> = {
  sh: '股东',
  lp: '法人',
  tm: '高管',
}

const formatDate = (value?: string) => {
  if (!value) return undefined
  const parsed = dayjs(value)
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : value
}

const formatMoney = (value?: string | number) => {
  if (!value && value !== 0) return undefined
  return typeof value === 'number' ? `¥${value}` : value
}

const renderField = (label: string, value?: string) => {
  if (!value) return null
  return (
    <div className="flex flex-col gap-1">
      <Text size="xs" c="dimmed">
        {label}
      </Text>
      <Text size="sm" className="text-gray-700" fw={500}>
        {value}
      </Text>
    </div>
  )
}

const renderRiskSection = (title: string, items: React.ReactNode[]) => {
  if (items.length === 0) return null
  return (
    <div className="flex flex-col gap-1">
      <Text size="xs" fw={600} className="text-gray-700">
        {title}
      </Text>
      <div className="flex flex-col gap-1 text-xs text-gray-600">{items}</div>
    </div>
  )
}

const CompanyCard: React.FC<CompanyCardProps> = ({ company, index }) => {
  const theme = useTheme()

  const relationships = company.relationship
    .map((rel) => relationshipLabelMap[rel] || rel)
    .filter(Boolean)
    .join('、')

  const dishonestRecords =
    company.dishonestExecutedPerson?.slice(0, 3).map((item, idx) => (
      <span key={idx}>
        {item.casecode || '案号未知'}，{item.courtname || '法院未知'}，立案 {formatDate(item.regdate)}
      </span>
    )) ?? []

  const executedRecords =
    company.executedPerson?.slice(0, 3).map((item, idx) => (
      <span key={idx}>
        {item.caseCode || '案号未知'}，执行标的 {formatMoney(item.execMoney) || '—'}，法院 {item.execCourtName || '—'}
      </span>
    )) ?? []

  const penaltyRecords =
    company.adminPenalty?.slice(0, 3).map((item, idx) => (
      <span key={idx}>
        {item.reason || '处罚事由未知'}，单位 {item.departmentName || '—'}，日期 {formatDate(item.decisionDate)}
      </span>
    )) ?? []

  return (
    <div
      className="rounded-lg border px-4 py-4"
      style={{ borderColor: theme.surfaceBorderColor, backgroundColor: theme.surfaceColor }}
    >
      <Stack gap="sm">
        <Text size="xs" c="dimmed">
          企业 {index}
        </Text>
        <Text size="sm" fw={600} className="text-gray-800">
          {company.orgName || '—'}
        </Text>
        {relationships && (
          <Text size="xs" c="dimmed">
            关联关系：{relationships}
          </Text>
        )}

        <div className="grid gap-3 md:grid-cols-2">
          {renderField('统一社会信用代码', company.basicInfo.creditCode)}
          {renderField('注册资本', company.basicInfo.regCapital)}
          {renderField('成立日期', formatDate(company.basicInfo.estiblishTime))}
          {renderField('法定代表人', company.basicInfo.legalPersonName)}
          {renderField('注册状态', company.basicInfo.regStatus)}
          {renderField('所属地区', company.basicInfo.base)}
        </div>

        {renderRiskSection('失信被执行人', dishonestRecords)}
        {renderRiskSection('被执行人记录', executedRecords)}
        {renderRiskSection('行政处罚', penaltyRecords)}
      </Stack>
    </div>
  )
}

export default CompanyCard
